import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { firstValueFrom, from, Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { BasedataService } from "../../shared/basedata.service";
import { Plan, PlanDto } from "./plan.interface";
import { PlanVersion, PlanVersionDto } from "./plan-versions.interface";
import { StructureTree } from "../structure/structure.model";
import { PlanCalibrationDto, PlanTransform } from "./plan-calibration.dto";
import { ProjectStructureService, ProjectStructureTree } from "@mydocma/project";
import { BpConfigService } from "../../config/bp-config.service";

@Injectable({providedIn: 'root'})
export class PlanApiService extends BasedataService {

  endpoint: string;

  constructor(
    private http: HttpClient,
    configService: BpConfigService,
    private readonly projectStructureService: ProjectStructureService,
  ) {
    super(configService);
  }

  createPlan(plan: Plan, file: Blob, scale?: number): Observable<Plan>{
    const formData = new FormData();
    formData.append('file',file);
    formData.append('title', plan.title);
    if ( plan.description !== null ) {
      formData.append('description', plan.description);
    }
    formData.append('fileDate', new Date(plan.versions[0].fileDate).toISOString());
    if ( plan.externalId !== null ) {
      formData.append('externalId', plan.externalId);
    }
    formData.append('structureId', plan.structureId);
    formData.append('projectId', plan.projectId);

    if(scale) formData.append('scale', scale.toString())

    return this.http.post<Plan>(this.getFullUrl('plans'), formData).pipe(
      switchMap(async plan => {
        plan.currentVersion = this.getCurrentVersion(plan);
        plan.currentVersion.planId = plan.id;
        plan.currentVersion.planTitle = plan.title;
        plan.structure = null;
        plan.structurePath = null;

        await this.saveStructureAndStructurePath(plan);
        return plan;
      }),
      switchMap(plan => from([plan]))
    );
  }

  updatePlan(plan: Plan): Observable<Plan> {
    return this.http.patch<Plan>(`${this.getFullUrl('plans')}/${plan.id}`, plan).pipe(
      switchMap(async responsePlan => {
        responsePlan.currentVersion = this.getCurrentVersion(responsePlan);
        responsePlan.currentVersion.planId = plan.id;
        responsePlan.currentVersion.planTitle = plan.title;
        responsePlan.structure = null;
        responsePlan.structurePath = null;

        await this.saveStructureAndStructurePath(responsePlan);
        return responsePlan;
      }),
      switchMap(responsePlan => from([responsePlan]))
    );
  }


  getPlans(): Observable<Plan[]> {
    return this.http.get<PlanDto[]>(this.getFullUrl('plans')).pipe(
      switchMap(planDtos =>
        from(Promise.all(
          planDtos.map(async (dto: PlanDto) => {
            const plan = dto as Plan;
            plan.currentVersion = this.getCurrentVersion(dto) || {};
            plan.currentVersion.planId = plan.id;
            plan.currentVersion.planTitle = plan.title;
            plan.structure = null;
            plan.structurePath = null;

            await this.saveStructureAndStructurePath(plan);
            return plan;
          })
        ))
      )
    );
  }

  public async saveStructureAndStructurePath(plan) {
    let tree: StructureTree = await firstValueFrom(this.projectStructureService.currentProjectStructureTree$$);
    plan = this.findPlanStructure( tree, plan );
  }

  private async findPlanStructure( tree: ProjectStructureTree, plan: Plan) {
    if (tree !== null) {
        if (plan.structureId !== null) {
            if (tree.id === plan.structureId) {
              plan.structure = tree.name + (!!tree?.desc ? ` - ${tree.desc}` : "");
              plan.structurePath = tree.path;
            } else {
              const node = await ProjectStructureTree.getNodeById(tree, plan.structureId);
              if (node !== null && node.id === plan.structureId) {
                  plan.structure = node.name + (!!node?.desc ?` - ${node.desc}`:"");
                  plan.structurePath = node.path;
              } else {
                  plan.structure = 'Don\'t have a structure for this plan'
                  plan.structurePath = 'Don\'t have a structure for this plan'
              }
            }
        } else {
            plan.structure = 'don\'t have a structure id'
            plan.structurePath = 'don\'t have a structure id'
        }
    }

    return plan;
  }

  getPlanById(planId: string, checkInUse = false): Observable<Plan> {
    let url = `${this.getFullUrl('plans')}/${planId}`;
    if (checkInUse) url += '?inUse=true';

    return this.http.get<Plan>(url).pipe(
      switchMap(async responsePlan => {
        responsePlan.currentVersion = this.getCurrentVersion(responsePlan);
        responsePlan.currentVersion.planId = planId;
        responsePlan.currentVersion.planTitle = responsePlan.title;
        responsePlan.structure = null;
        responsePlan.structurePath = null;

        await this.saveStructureAndStructurePath(responsePlan);
        return responsePlan;
      }),
      switchMap(responsePlan => from([responsePlan]))
    );
  }

  public getCurrentVersion(plan: PlanDto) {
    const versions = plan.versions;
    let currentIndex = 0;
    let currentVersion = versions[0];
    versions.map(version => {
        if (version.version > currentIndex) {
            currentIndex = version.version
            currentVersion = version;
        }
    })

    return currentVersion;
  }

  public getBeforeCurrentVersion(plan: PlanDto, cVersion: PlanVersion) {
    const versions = plan.versions;
    let currentIndex = 0;
    let currentVersion = versions[0];
    versions.map(version => {
        if (version.version > currentIndex && version.version !== cVersion.version) {
            currentIndex = version.version
            currentVersion = version;
        }
    })

    return currentVersion;
  }

  getFile(url) {
    return this.http.get<File>(url, { responseType: 'blob' as 'json'});
  }

  deletePlan(plan: Plan): Observable<Plan> {
    return this.http.delete<Plan>(`${this.getFullUrl('plans')}/${plan.id}`);
  }

  deletePlanVersion(plan: Plan, version: PlanVersion): Observable<Plan> {
    return this.http.delete<Plan>(`${this.getFullUrl('plans')}/${plan.id}/version/${version.id}`);
  }

  getPlanVersion(planId: string, version: PlanVersion) {
    return this.http.get<Plan>(`${this.getFullUrl('plans')}/${planId}/version/${version.id}`);
  }

  createPlanVersion( plan: Plan, file: Blob, planTitle: string, scale: string ): Observable<PlanVersion> {
    const formData = new FormData();
    formData.append('planId', plan.id);
    formData.append('title', planTitle);
    formData.append('scale', scale);
    formData.append('fileDate', new Date(file['lastModifiedDate']).toISOString());
    formData.append('file',file);

    return  this.http.post<PlanVersion>(`${this.getFullUrl('plans')}/${plan.id}/version`, formData)
    .pipe(
      map(version => {
        version.planId = plan.id;
        version.planTitle = plan.title;
        return version
      })
    );
  }

  updatePlanVersion(plan: Plan, version: PlanVersion): Observable<PlanVersion> {
    return this.http.patch<PlanVersion>(`${this.getFullUrl('plans')}/${plan.id}/version/${version.id}`, version)
    .pipe(
      map(version => {
        version.planId = plan.id;
        version.planTitle = plan.title;
        return version
      })
    );
  }

  getPlanVersionThumbnail(plan: Plan, planVersion: PlanVersion){
    return this.http.get<File>(`${this.getFullUrl('plans')}/${plan.id}/version/${planVersion.id}/thumbnail`,  { responseType: 'blob' as 'json'});
  }

  calibratePlanVersion(plan: Plan) {
    return  this.http.post<Plan>(`${this.getFullUrl('plans')}/calibration/plan`, plan)
    .pipe(
      map(plan=> {
        plan.currentVersion = this.getCurrentVersion(plan);
        plan.currentVersion.planId = plan.id;
        plan.currentVersion.planTitle = plan.title;
        return plan
      })
    );
  }

  calculatePlanCalibration(data: PlanCalibrationDto): Observable<PlanTransform> {
    const url = `${this.getFullUrl('plans')}/calibration/plan`
    return this.http.post<PlanTransform>(url, data);
  }
}
